/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import MuiAlert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';

import useIMS, {
  STEP_ERROR_LOGGED,
  STEP_FETCHING,
  STEP_LOGGED,
} from './hooks/useIMS';

const styles = () => ({
  container: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  title: {
    width: '400px',
    color: '#777777',
    textAlign: 'center',
    marginBottom: '20px',
  },
  logo: {
    width: '150px',
  },
  adobeLogo: {
    width: '20px',
  },
  alert: {
    top: '0',
    position: 'absolute',
    margin: '20px 0',
  },
});

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const LoginPage = ({ classes }) => {
  const { step, error, login, logout } = useIMS();
  const [errorMessage, setErrorMessage] = useState();

  const AdobeLogo = (
    <img
      alt={'Adobe'}
      className={classes.adobeLogo}
      src={`/images/adobe_logo.svg`}
    />
  );

  useEffect(() => {
    if (error) {
      let message;
      switch (error.reason) {
        case 'notauthorized':
          message =
            'Your are not authorize to use this application. Please disconnect and try use another account.';
          break;
        case 'technical':
          message =
            'Something went wrong will getting your information. Please try again.';
          break;
        case 'user':
          message =
            'We were not able to get your information. Please disconnect and try use another account.';
          break;
        default:
          message = 'An unknown error occured.';
          break;
      }
      setErrorMessage(message);
    }
  }, [error]);

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <img
          src={`/images/source_logo.svg`}
          alt={'Source'}
          className={classes.logo}
        />
        <Typography variant="h6" gutterBottom>
          ADMIN
        </Typography>
        <Divider variant="fullWidth" />
      </div>

      {step === STEP_FETCHING || step === STEP_LOGGED ? (
        <CircularProgress size={75} />
      ) : step === STEP_ERROR_LOGGED ? (
        <Button variant="contained" onClick={logout}>
          Logout
        </Button>
      ) : (
        <Button variant="contained" startIcon={AdobeLogo} onClick={login}>
          Login with Adobe
        </Button>
      )}

      {errorMessage && (
        <Alert className={classes.alert} severity="error">
          {errorMessage}
        </Alert>
      )}
    </div>
  );
};

export default withStyles(styles)(LoginPage);
